import React from 'react';
import {css, styled} from 'styled-components';
import {colors} from 'styles/colors';
import DefaultImageView from './DefaultImageView';
import moment from 'moment-timezone';
import {useFormatMsg} from 'utils/useFormatMsg';
import {getDevice} from 'utils/getDevice';
import {minmax} from 'utils/minmax';

/**
 * 공통 모달 컴포넌트의 Props 인터페이스
 * @property {string | JSX.Element} [children] - 모달 내용
 * @property {string} [title] - 모달 제목
 * @property {React.CSSProperties} [titleStyle] - 제목 스타일
 * @property {string | JSX.Element | string[]} [detail] - 모달 상세 내용
 * @property {React.CSSProperties} [detailStyle] - 상세 내용 스타일
 * @property {React.CSSProperties} [containerStyle] - 컨테이너 스타일
 * @property {string} [firstButtonText] - 첫 번째 버튼 텍스트
 * @property {React.CSSProperties} [firstButtonStyle] - 첫 번째 버튼 스타일
 * @property {() => void} [firstButtonClick] - 첫 번째 버튼 클릭 핸들러
 * @property {string} [secondButtonText] - 두 번째 버튼 텍스트
 * @property {React.CSSProperties} [secondButtonStyle] - 두 번째 버튼 스타일
 * @property {() => void} [secondButtonClick] - 두 번째 버튼 클릭 핸들러
 * @property {() => void} [closeModal] - 모달 닫기 핸들러
 * @property {() => void} [innerCloseModal] - 내부 닫기 핸들러
 * @property {boolean} [closeImage] - 닫기 이미지 표시 여부
 * @property {any} [startTime] - 시작 시간
 * @property {any} [endTime] - 종료 시간
 * @property {any} [childStatusHistoryList] - 자식 상태 히스토리 목록
 */
export interface ICommonModal {
  children?: string | JSX.Element;
  title?: string;
  titleStyle?: React.CSSProperties;
  detail?: string | JSX.Element | string[];
  detailStyle?: React.CSSProperties;
  containerStyle?: React.CSSProperties;

  firstButtonText?: string;
  firstButtonStyle?: React.CSSProperties;
  firstButtonClick?: () => void;

  secondButtonText?: string;
  secondButtonStyle?: React.CSSProperties;
  secondButtonClick?: () => void;

  closeModal?: () => void;
  innerCloseModal?: () => void;
  closeImage?: boolean;

  startTime?: any;
  endTime?: any;
  childStatusHistoryList?: any;
}

/**
 * 공통 모달 컴포넌트
 * 공통으로 사용되는 모달 컴포넌트입니다.
 * @param {ICommonModal} props - 모달 컴포넌트의 props
 * @returns {JSX.Element} 공통 모달 컴포넌트
 */
const CommonModal = ({
  children,
  title,
  titleStyle,
  detail,
  detailStyle,
  firstButtonText,
  firstButtonStyle,
  secondButtonText,
  secondButtonStyle,
  firstButtonClick,
  secondButtonClick,
  closeModal,
  innerCloseModal,
  closeImage,
  startTime,
  endTime,
  childStatusHistoryList,
  containerStyle,
}: ICommonModal) => {
  const device = getDevice();

  const titleText = title && (useFormatMsg(title) || title);
  const detailText =
    typeof detail === 'string' && (useFormatMsg(detail) || detail);
  const firstButtonTxt =
    firstButtonText && (useFormatMsg(firstButtonText) || firstButtonText);
  const secondButtonTxt =
    secondButtonText && (useFormatMsg(secondButtonText) || secondButtonText);

  return (
    <Wrapper onClick={closeModal}>
      <Container
        $device={device}
        style={containerStyle}
        onClick={(e: any) => e.stopPropagation()}>
        {children && !title ? children : null}
        {!children && title && (
          <>
            {closeImage && (
              <DefaultImageView
                source="images/common/close.png"
                onClick={innerCloseModal}
                style={{
                  width: 'max(40px, min(1vw, 60px))',
                  position: 'absolute',
                  top: '15px',
                  right: '15px',
                  cursor: 'pointer',
                }}
              />
            )}
            <TitleText style={titleStyle}>{titleText}</TitleText>
            {childStatusHistoryList && childStatusHistoryList.length > 0 && (
              <DetailText style={detailStyle}>{`종료예정 시간: ${moment(
                childStatusHistoryList[childStatusHistoryList.length - 1]
                  .estimatedCompletionTime,
              )
                .format('YYYY-MM-DD HH:mm:ss')
                .toString()}`}</DetailText>
            )}
            {childStatusHistoryList && childStatusHistoryList.length > 0 ? (
              <div
                style={{
                  overflow: 'auto',
                  maxHeight: '10vh',
                  marginBottom: 'max(28px, min(2vw, 34px))',
                }}>
                <div style={{marginBottom: 'max(18px, min(2vw, 24px))'}}>
                  <DetailText style={detailStyle}>{detail}</DetailText>
                  <div>
                    <DetailText style={detailStyle}>
                      {moment(startTime)
                        .format('YYYY-MM-DD HH:mm:ss')
                        .toString()}
                    </DetailText>
                    <DetailText style={detailStyle}>{' ~ '}</DetailText>
                    <DetailText style={detailStyle}>
                      {moment(endTime).format('YYYY-MM-DD HH:mm:ss').toString()}
                    </DetailText>
                  </div>
                </div>
                {childStatusHistoryList.map((item: any, index: any) => (
                  <div
                    key={index}
                    style={{marginBottom: 'max(18px, min(2vw, 24px))'}}>
                    <DetailText style={detailStyle}>
                      {item.description}
                    </DetailText>
                    <div>
                      <DetailText style={detailStyle}>
                        {moment(item.issuedAt)
                          .format('YYYY-MM-DD HH:mm:ss')
                          .toString()}
                      </DetailText>
                      <DetailText style={detailStyle}>{' ~ '}</DetailText>
                      <DetailText style={detailStyle}>
                        {moment(item.estimatedCompletionTime)
                          .format('YYYY-MM-DD HH:mm:ss')
                          .toString()}
                      </DetailText>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              (detailText || startTime) && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    width: '100%',
                  }}>
                  <DetailText style={detailStyle}>{detailText}</DetailText>
                  {startTime && (
                    <div style={{marginBottom: 'max(28px, min(2vw, 34px))'}}>
                      <DetailText style={detailStyle}>
                        {moment(startTime)
                          .format('YYYY-MM-DD HH:mm:ss')
                          .toString()}
                      </DetailText>
                      <DetailText style={detailStyle}>{' ~ '}</DetailText>
                      <DetailText style={detailStyle}>
                        {moment(endTime)
                          .format('YYYY-MM-DD HH:mm:ss')
                          .toString()}
                      </DetailText>
                    </div>
                  )}
                </div>
              )
            )}
            <ButtonContainer>
              <FirstButton style={firstButtonStyle} onClick={firstButtonClick}>
                {firstButtonTxt}
              </FirstButton>
              {secondButtonText && (
                <SecondButton
                  style={secondButtonStyle}
                  onClick={secondButtonClick}>
                  {secondButtonTxt}
                </SecondButton>
              )}
            </ButtonContainer>
          </>
        )}
        {children && title && (
          <>
            <TitleText style={titleStyle}>{titleText}</TitleText>
            {children}
            <ButtonContainer>
              <FirstButton style={firstButtonStyle} onClick={firstButtonClick}>
                {firstButtonTxt}
              </FirstButton>
              {secondButtonText && (
                <SecondButton
                  style={secondButtonStyle}
                  onClick={secondButtonClick}>
                  {secondButtonTxt}
                </SecondButton>
              )}
            </ButtonContainer>
          </>
        )}
      </Container>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.35);
  z-index: 1000000;
`;

const Container = styled.div<{$device?: string}>`
  /* width: 36%; */
  min-width: ${minmax(680)};
  /* min-height: ${minmax(294)}; */
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-width: 100%;
  max-height: 90%;
  overflow-y: auto;
  background-color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: max(8px, min(2vw, 16px));
  padding: ${minmax(40)};
  /* overflow: visible; */
  overflow: hidden;

  ${(props: any) =>
    props.$device === 'mobile' &&
    css`
      width: 90%;
      min-width: auto;
    `}
`;

const ButtonContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`;

const TitleText = styled.p`
  font-size: max(18px, min(2vw, 36px));
  font-weight: 500;
  letter-spacing: -1.08px;
  color: ${colors.black};
  margin-bottom: max(28px, min(2vw, 34px));
  text-align: center;
`;

const DetailText = styled.span`
  font-size: max(16px, min(2vw, 32px));
  font-style: normal;
  font-weight: 400;
  letter-spacing: -0.96px;
  color: ${colors.black};
  margin-bottom: max(33px, min(2vw, 55px));
  text-align: center;
`;

const FirstButton = styled.button`
  border-radius: ${minmax(10)};
  background-color: ${colors.primary2};
  flex: 1;
  height: ${minmax(60)};
  line-height: ${minmax(60)};
  font-size: max(12px, min(2vw, 24px));
  color: ${colors.white};
`;

const SecondButton = styled.button`
  border-radius: ${minmax(10)};
  background-color: ${colors.darkGray};
  flex: 1;
  height: ${minmax(60)};
  line-height: ${minmax(60)};
  margin-left: max(20px, min(2vw, 30px));
  font-size: max(12px, min(2vw, 24px));
  color: ${colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default CommonModal;
